/* eslint-disable max-lines */
import React, { useCallback, useEffect, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DialogStep, FCWithFragments } from '@modernloop/shared/components';
import { CaretLeftIcon } from '@modernloop/shared/icons';
import { Alert, Typography } from '@mui/material';
import { uniq } from 'lodash';
import { useSnackbar } from 'notistack';

import {
  DynamicAudience,
  NotificationPreferenceInput,
  SelfScheduleEmail_JobStageSettingsV2Fragment,
  TemplateType,
  useCandidateDetailsQuery,
} from 'src/generated/mloop-graphql';

import { SmartEmployeeSelectResult } from 'src/entities/Select/SmartEmployeeSelect';

import { FileUploadFlow, removeAllUploadedFiles } from 'src/slices/files';

import { hasCandidatePortalUrl } from 'src/urls/hasCandidatePortalUrl';
import { hasShortUrl } from 'src/urls/hasShortUrl';

import { SelfScheduleTokens } from 'src/utils/PlaceholderFiller/useTemplateTokens';

import EmailContent, { EmailContentTemplate } from 'src/views-new/EmailContent/index';

import { TEMPLATE_TOKEN_REGEX } from 'src/constants';
import { useDispatch } from 'src/store';

import MissingSelfScheduleLinkTokenModal from './MissingSelfScheduleLinkTokenModal';
import useSelfScheduleOptionsEmailFiller from './useSelfScheduleOptionsEmailFiller';
import useSelfScheduleOptionsUpsert from './useSelfScheduleOptionsUpsert';

type Props = {
  applicationId: string;
  candidateId: string;
  jobStageId: string;
  subTitle?: string;
  selfScheduleOptionsUpsert: ReturnType<typeof useSelfScheduleOptionsUpsert>;
  goBack?: () => void;
  onCancel?: () => void;
  onSave: (emailSent: boolean, taskId: string | null) => void;
  canSaveWithoutSending?: boolean;
  taskId?: string;
  assigneeId?: string;
  subscriberEmployeeIds?: string[];
  jobId?: string;
  taskCreatorId?: string;
  ccRecipients?: NotificationPreferenceInput | null;
  bccRecipients?: NotificationPreferenceInput | null;
};

type Fragments = {
  jobStageSettingsV2: SelfScheduleEmail_JobStageSettingsV2Fragment;
};

const SelfScheduleEmail: FCWithFragments<Fragments, Props> = ({
  applicationId,
  candidateId,
  jobStageId,
  subTitle,
  selfScheduleOptionsUpsert,
  goBack,
  onCancel,
  onSave,
  subscriberEmployeeIds,
  jobStageSettingsV2,
  jobId,
  canSaveWithoutSending = true,
  taskId,
  assigneeId,
  taskCreatorId,
  ccRecipients,
  bccRecipients,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const [ccEmployeeIds, setCcEmployeeIds] = useState<string[]>(uniq(ccRecipients?.employeeIds || []));
  const [bccEmployeeIds, setBccEmployeeIds] = useState<string[]>(bccRecipients?.employeeIds || []);
  const [ccDynamicAudience, setCcDynamicAudience] = useState<DynamicAudience[]>(ccRecipients?.dynamicAudiences || []);

  const [bccDynamicAudience, setBccDynamicAudience] = useState<DynamicAudience[]>(
    bccRecipients?.dynamicAudiences || []
  );
  const [toFreeform, setToFreeform] = useState<string[]>([]);
  const [ccFreeform, setCcFreeform] = useState<string[]>(ccRecipients?.externalEmailAddresses || []);
  const [bccFreeform, setBccFreeform] = useState<string[]>(bccRecipients?.externalEmailAddresses || []);
  const [selectedToFreeformEmail, setSelectedToFreeformEmail] = useState<string[]>([]);

  const [showMissingTokenModal, setShowMissingTokenModal] = useState(false);
  const [isMissingSelfScheduleLinkToken, setIsMissingSelfScheduleLinkToken] = useState(false);

  const [sendingEmail, setSendingEmail] = useState(false);
  const [savingOptionsWithoutCandidateCommunication, setSavingOptionsWithoutCandidateCommunication] = useState(false);
  const [error, setError] = useState<string>();

  const [subject, setSubject] = useState<string>('');
  const [bodyHtml, setBodyHtml] = useState<string>('');

  const { data: candidateData, loading: loadingCandidateDetails } = useCandidateDetailsQuery({
    variables: { id: candidateId },
  });

  const selfScheduleOptionsEmailFiller = useSelfScheduleOptionsEmailFiller(
    applicationId,
    jobStageId,
    candidateData,
    taskId,
    assigneeId
  );

  const [selectedTemplate, setSelectedTemplate] = useState(
    jobStageSettingsV2.selfScheduleRequestEmailTemplateId?.template ?? undefined
  );

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect runs imperative data work when dependencies change; prefer a data hook when possible.
  useEffect(() => {
    dispatch(removeAllUploadedFiles());
  }, [dispatch]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    const additionalEmails = (
      candidateData?.candidate?.additionalEmails ? [...candidateData.candidate.additionalEmails] : []
    ) as string[];

    const preferredEmail = (candidateData?.candidate?.email ||
      candidateData?.candidate?.additionalEmails?.[0]) as string;

    // candidate.email might not exist in the list from the ATS (candidate.additionalEmails)
    // if it does, use the ATS list directly
    // if it doesn't, expand the list with the candidate.email as the first item
    let emailList: string[] = [];
    if (additionalEmails.indexOf(preferredEmail) > -1) {
      emailList = additionalEmails;
    } else {
      if (additionalEmails.length) {
        emailList = additionalEmails;
      }
      if (preferredEmail) {
        emailList = [preferredEmail, ...emailList];
      }
    }
    setToFreeform(emailList);
    setSelectedToFreeformEmail(preferredEmail ? [preferredEmail] : []);
  }, [candidateData]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    setSubject(selfScheduleOptionsEmailFiller(selectedTemplate?.subject || ''));
    setBodyHtml(selfScheduleOptionsEmailFiller(selectedTemplate?.body || ''));
  }, [selectedTemplate, selfScheduleOptionsEmailFiller]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    setIsMissingSelfScheduleLinkToken(bodyHtml.indexOf(SelfScheduleTokens.SELF_SCHEDULE_LINK) === -1);
  }, [bodyHtml]);

  const handleTo = (freeformEmails: string[]) => {
    setSelectedToFreeformEmail(freeformEmails);
  };

  const handleCc = (result: SmartEmployeeSelectResult) => {
    setCcEmployeeIds(result.selectedEmployeeIds);
    setCcFreeform(result.selectedEmailAddresses);
    setCcDynamicAudience(result.dynamicAudiences);
  };

  const handleBcc = (result: SmartEmployeeSelectResult) => {
    setBccEmployeeIds(result.selectedEmployeeIds);
    setBccFreeform(result.selectedEmailAddresses);
    setBccDynamicAudience(result.dynamicAudiences);
  };

  const handleEmailTemplateChange = (template: EmailContentTemplate) => {
    if (!template || template.id === selectedTemplate?.id) {
      return;
    }
    setSelectedTemplate(template);
    setSubject(template.subject || '');
    setBodyHtml(template.body || '');
    setIsMissingSelfScheduleLinkToken(template.body?.indexOf(SelfScheduleTokens.SELF_SCHEDULE_LINK) === -1);
  };

  const handleSendSelfScheduleLinkEmail = useCallback(
    (bypassSelfScheduleLinkTokenCheck: boolean, saveWithoutSending = false) => {
      if (!candidateData) {
        return;
      }

      const bodyHasPortalUrl = hasCandidatePortalUrl(bodyHtml) || hasShortUrl(bodyHtml);

      const skipTokenCheck = bodyHasPortalUrl || bypassSelfScheduleLinkTokenCheck;

      // if this is being called from MissingSelfScheduleLinkTokenModal, we can skip this check because we don't want to show the modal again
      if (!skipTokenCheck && isMissingSelfScheduleLinkToken) {
        setShowMissingTokenModal(true);
        return;
      }

      if (saveWithoutSending) {
        setSavingOptionsWithoutCandidateCommunication(true);
      } else {
        setSendingEmail(true);
      }

      // eslint-disable-next-line promise/catch-or-return
      selfScheduleOptionsUpsert(
        selectedToFreeformEmail[0],
        ccFreeform,
        bccFreeform,
        ccEmployeeIds,
        bccEmployeeIds,
        selectedTemplate?.id,
        subject,
        `${bodyHtml}`,
        saveWithoutSending,
        ccDynamicAudience,
        bccDynamicAudience
      )
        .then((createdTaskId: string | null) => {
          onSave(!saveWithoutSending, createdTaskId);

          // eslint-disable-next-line promise/always-return
          enqueueSnackbar(`Self-schedule request ${saveWithoutSending ? 'saved' : 'sent'} successfully!`, {
            variant: 'success',
          });
        })
        .catch((reason) => {
          setError(reason.message);
          enqueueSnackbar('There was an error while creating the self-schedule request', {
            variant: 'error',
          });
        })
        .finally(() => {
          setSendingEmail(false);
          setSavingOptionsWithoutCandidateCommunication(false);
        });
    },
    [
      bccDynamicAudience,
      bccEmployeeIds,
      bccFreeform,
      bodyHtml,
      candidateData,
      ccDynamicAudience,
      ccEmployeeIds,
      ccFreeform,
      enqueueSnackbar,
      isMissingSelfScheduleLinkToken,
      onSave,
      selectedTemplate,
      selectedToFreeformEmail,
      selfScheduleOptionsUpsert,

      subject,
    ]
  );

  const bodyUnusedTokens = bodyHtml.match(TEMPLATE_TOKEN_REGEX) || [];
  const subjectUnusedTokens = subject.match(TEMPLATE_TOKEN_REGEX) || [];
  const unusedTokens =
    uniq([...bodyUnusedTokens, ...subjectUnusedTokens]).filter(
      (value) => value !== `{{${SelfScheduleTokens.SELF_SCHEDULE_LINK}}}`
    ) || [];
  const hasUnusedToken = !!unusedTokens.length;

  const getSendEmailDisabledTooltip = () => {
    if (hasUnusedToken) {
      return t('hiring-dashboard-schedule:schedule.selfScheduleEmail.tooltip.unusedTokens', {
        tokens: unusedTokens?.join(', '),
      });
    }

    if (selectedToFreeformEmail.length === 0) {
      return t('hiring-dashboard-schedule:schedule.selfScheduleEmail.tooltip.candidateEmailNotPresent');
    }

    return undefined;
  };

  return (
    <>
      <DialogStep
        title={t('hiring-dashboard-schedule:schedule.selfScheduleEmail.title')}
        subTitle={subTitle}
        submitOptions={{
          dataTestId: 'self-schedule-email-submit-button',
          label: goBack
            ? t('hiring-dashboard-schedule:schedule.selfScheduleEmail.submit.saveAndSendEmail')
            : t('hiring-dashboard-schedule:schedule.selfScheduleEmail.submit.sendEmail'),
          isDisabled:
            sendingEmail ||
            !subject ||
            !bodyHtml ||
            hasUnusedToken ||
            savingOptionsWithoutCandidateCommunication ||
            selectedToFreeformEmail.length === 0,
          isLoading: sendingEmail,
          tooltip: getSendEmailDisabledTooltip(),
          onClick: () => handleSendSelfScheduleLinkEmail(false),
        }}
        cancelOptions={
          goBack
            ? {
                label: t('hiring-dashboard-schedule:schedule.selfScheduleEmail.navigation.previousStep'),
                onClick: () => goBack(),
                startIcon: <CaretLeftIcon />,
              }
            : {
                label: t('hiring-dashboard-common:common.cancel'),
                onClick: onCancel || (() => {}),
              }
        }
        secondaryOptions={
          goBack
            ? {
                label: t('hiring-dashboard-schedule:schedule.selfScheduleEmail.secondary.saveWithoutSending'),
                tooltip: !canSaveWithoutSending
                  ? t('hiring-dashboard-schedule:schedule.selfScheduleEmail.tooltip.candidateEmailNotPresent')
                  : undefined,
                isDisabled:
                  !toFreeform.length ||
                  savingOptionsWithoutCandidateCommunication ||
                  sendingEmail ||
                  !canSaveWithoutSending,
                isLoading: savingOptionsWithoutCandidateCommunication,
                onClick: () =>
                  handleSendSelfScheduleLinkEmail(
                    true /* bypassSelfScheduleLinkTokenCheck */,
                    true /* saveWithoutSending */
                  ),
              }
            : undefined
        }
        onClose={onCancel}
      >
        <EmailContent
          assigneeEmployeeId={assigneeId}
          ccEmployeeIds={ccEmployeeIds}
          bccEmployeeIds={bccEmployeeIds}
          ccDynamicAudiences={ccDynamicAudience}
          bccDynamicAudiences={bccDynamicAudience}
          bccFreeformEmails={bccFreeform}
          applicationId={applicationId}
          taskId={taskId}
          subscriberEmployeeIds={subscriberEmployeeIds}
          taskCreatorId={taskCreatorId}
          ccFreeformEmails={ccFreeform}
          jobId={jobId}
          filterBccDynamicAudiences={[
            DynamicAudience.DebriefAttendees,
            DynamicAudience.InterviewersOnSchedule,
            DynamicAudience.CandidatesPreviousInterviewers,
          ]}
          filterCcDynamicAudiences={[
            DynamicAudience.DebriefAttendees,
            DynamicAudience.InterviewersOnSchedule,
            DynamicAudience.CandidatesPreviousInterviewers,
          ]}
          alerts={
            <Alert severity="info">
              <Typography variant="caption" fontWeight={400}>
                {t('hiring-dashboard-schedule:schedule.selfScheduleEmail.alert.tokenInfo', {
                  token: `{{SELF_SCHEDULE_LINK}}`,
                })}
              </Typography>
            </Alert>
          }
          sendError={error}
          loading={loadingCandidateDetails}
          subject={subject}
          bodyHtml={bodyHtml}
          fileUploadFlow={FileUploadFlow.SELF_SCHEDULE_LINK}
          templateType={TemplateType.SelfScheduleRequestCandidateEmail}
          selectedTemplateId={selectedTemplate?.id}
          selectedToFreeformEmail={selectedToFreeformEmail[0]}
          toFreeformEmails={toFreeform}
          defaultCcFreeformEmails={ccFreeform}
          defaultBccFreeformEmails={bccFreeform}
          defaultCcEmployeeIds={ccEmployeeIds}
          defaultBccEmployeeIds={bccEmployeeIds}
          onToChanged={handleTo}
          onCcChanged={handleCc}
          onBccChanged={handleBcc}
          onChangeSubject={setSubject}
          onChangeBodyHtml={setBodyHtml}
          onChangeTemplate={handleEmailTemplateChange}
        />
      </DialogStep>
      {showMissingTokenModal && (
        <MissingSelfScheduleLinkTokenModal
          goBack={() => setShowMissingTokenModal(false)}
          onAppendAndContinue={() => {
            setShowMissingTokenModal(false);
            handleSendSelfScheduleLinkEmail(true);
          }}
        />
      )}
    </>
  );
};

SelfScheduleEmail.fragments = {
  jobStageSettingsV2: gql`
    fragment SelfScheduleEmail_jobStageSettingsV2 on JobStageSettingsV2 {
      id
      selfScheduleRequestEmailTemplateId {
        templateId
        template {
          id
          ...TemplateSelectRow_template
        }
      }
    }
  `,
};

export default SelfScheduleEmail;
